import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Eye, ArrowLeft, Edit3, Save, XCircle, Send } from "lucide-react";

/**
 * Bandeau d'impersonation — affiché quand `?user_id=` est présent dans l'URL.
 * Iter-123: le CEO consulte en read-only par défaut puis clique sur "Edit" pour
 * débloquer l'édition (après saisie d'une raison). "Submit on behalf" reste dispo
 * pour CEO / MANAGER. Un MANAGER voit un bandeau bleu (read-only only).
 */
export default function ImpersonationBanner({
  canEditOtherRole,
  canSubmitOtherRole,
  editingOnBehalf,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onSubmitOnBehalf,
  targetUser,
  viewedUserId,
  missionSubmitted,
}) {
  const inEdit = !!editingOnBehalf;
  return (
    <div
      data-testid="timesheet-impersonation-banner"
      className={`mb-6 p-4 rounded-lg border flex flex-wrap items-center gap-3 ${
        inEdit
          ? "bg-brand-orange-100 border-brand-orange text-brand-navy"
          : "bg-blue-50 border-blue-200 text-blue-900"
      }`}
    >
      {inEdit
        ? <AlertTriangle size={18} className="text-brand-orange-600 shrink-0" />
        : <Eye size={18} className="text-blue-700 shrink-0" />}
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm font-medium">
          {inEdit ? "Édition au nom de" : "Read-only —"}{" "}
          <span className="font-semibold">{targetUser?.name || viewedUserId}</span>
          {targetUser?.email && (
            <span className="text-xs font-mono ml-2 opacity-70">{targetUser.email}</span>
          )}
        </div>
        <div className="text-xs opacity-80 mt-0.5">
          {inEdit
            ? `Edit mode active. Click "Save changes" when done — a reason will be requested and an email with the diff will be sent to ${targetUser?.name || "the user"}.`
            : (canEditOtherRole
              ? "Lecture seule. Cliquez Edit pour débloquer l'édition."
              : "Lecture seule (manager de mission).")}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 ml-auto">
        {!inEdit && canEditOtherRole && (
          <button
            type="button"
            onClick={onStartEdit}
            data-testid="impersonation-start-edit"
            className="inline-flex items-center gap-1.5 px-3 h-8 bg-brand-navy text-white text-xs rounded hover:bg-brand-navy/90"
          >
            <Edit3 size={13} /> Edit
          </button>
        )}
        {inEdit && (
          <>
            <button
              type="button"
              onClick={onCancelEdit}
              data-testid="impersonation-cancel-edit"
              className="inline-flex items-center gap-1.5 px-3 h-8 bg-white border border-current/30 text-xs rounded hover:bg-white/80"
            >
              <XCircle size={13} /> Cancel
            </button>
            <button
              type="button"
              onClick={onSaveEdit}
              data-testid="impersonation-save-edit"
              className="inline-flex items-center gap-1.5 px-3 h-8 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700"
            >
              <Save size={13} /> Save changes
            </button>
          </>
        )}
        {/* Iter-228x-bis — "Submit on behalf" doit rester accessible même en
            mode édition. Précédemment caché derrière `!inEdit`, ce qui forçait
            l'utilisateur à sortir du mode édition avant de pouvoir soumettre. */}
        {canSubmitOtherRole && !missionSubmitted && (
          <button
            type="button"
            onClick={onSubmitOnBehalf}
            data-testid="impersonation-submit-on-behalf"
            className="inline-flex items-center gap-1.5 px-3 h-8 bg-zinc-950 text-white text-xs rounded hover:bg-zinc-800"
            title="Submit this month's CRA on the user's behalf"
          >
            <Send size={13} /> Submit on behalf
          </button>
        )}
        <Link
          to="/timesheet"
          data-testid="exit-impersonation"
          className="inline-flex items-center gap-1.5 px-3 h-8 bg-white border border-current/20 rounded text-xs hover:bg-white/80"
        >
          <ArrowLeft size={12} /> Mon timesheet
        </Link>
      </div>
    </div>
  );
}
